import { Component, Output, EventEmitter } from '@angular/core';
import { AllowedAccessLevels } from '../..';
import { ValueMetricsProductType } from '../../../constants-types';
import {
    PartnerInformation,
    User,
    TransactionTableEntry,
    SoftwareModuleAccessConfiguration,
    ValueMetricLimit,
    ModuleName,
    ModuleAccountType
} from '../../../core-data-model';
import {
    ValueMetricsService,
    CompanySettingsApi,
    PartnerInformationService
} from '../../../core-services';
import { TranslationService } from '../../../translations';

@Component({
    selector: 'account-subscription-overview-component',
    template: `
        <div class="detail-flex">
            <div>
                <subscription-plans
                    [softwareModuleAccessConfig]="softwareModuleAccessConfig"
                    [vmConfigs]="vmConfigs"
                ></subscription-plans>
            </div>
            <div>
                <billing-contact-details
                    [billingContact]="billingContact"
                ></billing-contact-details>
            </div>
        </div>
        <br />
        <div class="detail-flex">
            <div>
                <subscription-activations
                    [valueMetrics]="vmConfigs"
                ></subscription-activations>
            </div>
            <div>
                <consultant-contact-details
                    [partnerInfo]="partnerInfo"
                    [website]="consultantWebsite"
                ></consultant-contact-details>
            </div>
        </div>
        <br />
        <div class="detail-flex">
            <div></div>
            <div>
                <contract-renewal-date
                    [contractRenewalDate]="contractRenewalDate"
                ></contract-renewal-date>
            </div>
        </div>
        <br />
        <div>
            <transaction-table-component [vmConfigs]="vmConfigs">
            </transaction-table-component>
        </div>
    `,
    styleUrls: ['./overview.component.less'],
    standalone: false
})
export class AccountSubscriptionManagerOverviewComponent {
    @Output()
    initComplete = new EventEmitter<boolean>();
    partnerInfo: PartnerInformation;
    subscriptionPlans: string[];
    consultantWebsite: string;
    allTransactions: TransactionTableEntry[] = [];
    tableData: TransactionTableEntry[] = [];
    users: User[] = [];
    softwareModuleAccessConfig: SoftwareModuleAccessConfiguration;
    vmConfigs: ValueMetricLimit[];

    constructor(
        private partnerInfoService: PartnerInformationService,
        private companySettingsApi: CompanySettingsApi,
        private VM: ValueMetricsService,
        public TR: TranslationService
    ) {
        Promise.all([this.getPartnerInfo(), this.getSubscriptionPlans()]).then(
            () => {
                if (
                    this.partnerInfo?.companyName?.trim() ===
                    'The Predictive Index'
                )
                    this.consultantWebsite = 'www.predictiveindex.com';
                this.initComplete.emit(true);
            }
        );
    }

    get billingContact(): string {
        return this.partnerInfo?.accountMetadata.find(
            (m) => m.name === 'billingContactName'
        )?.value;
    }

    get contractRenewalDate(): string {
        const metadataObj = this.partnerInfo?.accountMetadata.find(
            (m) => m.name === 'contractEndDate'
        );
        const contractRenewal = metadataObj ? metadataObj.value : null;

        if (this.isPaidAccount && !metadataObj?.value) {
            return this.TR.key('billing-manager.billing-detail.error');
        }
        return contractRenewal !== null
            ? new Date(contractRenewal).toLocaleDateString()
            : this.TR.key('billing-manager.details.no-contract');
    }

    get isPaidAccount(): boolean {
        return (
            this.softwareModuleAccessConfig.softwareModuleAccessConfig.findIndex(
                (module) => module.moduleAccountType === ModuleAccountType.Paid
            ) >= 0
        );
    }

    async getPartnerInfo() {
        this.partnerInfo = this.partnerInfo
            ? this.partnerInfo
            : await this.partnerInfoService.getPartnerInformationForCurrentCompany();
    }

    async getSubscriptionPlans() {
        this.softwareModuleAccessConfig =
            await this.companySettingsApi.getCompanySoftwareModuleAccessConfiguration();
        this.vmConfigs = await this.VM.getValueMetricConfigs();
        this.vmConfigs = this.vmConfigs.filter(
            (config) =>
                this.softwareModuleAccessConfig.softwareModuleAccessConfig.find(
                    (module) =>
                        AllowedAccessLevels.some(
                            (accessLevel) => accessLevel === module.accessLevel
                        ) &&
                        ModuleName[module.name] ===
                            ValueMetricsProductType[config.product]
                ) !== undefined && config.limit !== 0
        );
    }
}
